
/**
 * Pure text normalization - removes extra whitespace and normalizes line breaks
 */
export const normalizeText = (text: string): string => {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+/g, ' ')
    .trim();
};

/**
 * Pure text sanitization for TTS - removes section labels and formatting
 */
export const sanitizeForTTS = (text: string): string => {
  const normalized = normalizeText(text);
  
  return normalized
    // Remove JSON artifacts and code blocks
    .replace(/^```(?:\w+)?\s*$/gm, '')
    .replace(/^```\s*$/gm, '')
    .replace(/^\s*json\s*$/gm, '')
    .replace(/[{}[\]"']/g, '')
    .replace(/^\s*[\w_]+:\s*/gm, '') // Remove JSON keys like "summary:"
    // Remove section labels like [English], [Chinese]
    .replace(/^\s*\[.*?\]\s*$/gm, '')
    // Remove Chinese section labels
    .replace(/^\s*【.*?】\s*$/gm, '')
    // Remove markdown headers
    .replace(/^#{1,6}\s+/gm, '')
    // Remove bullet points and list markers
    .replace(/^\s*[-*+•]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    // Remove emphasis markers
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/_(.*?)_/g, '$1')
    // Remove inline code
    .replace(/`([^`]+)`/g, '$1')
    // Remove links
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Remove special characters and symbols (except basic punctuation)
    .replace(/[*#`~_\[\]{}()<>]/g, '')
    // Remove JSON punctuation
    .replace(/[,;]/g, ' ')
    // Clean up multiple spaces and line breaks
    .replace(/\n{2,}/g, '\n\n')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

/**
 * Pure duration estimation in seconds based on average reading speed
 */
export const estimateDuration = (text: string): number => {
  const cleanText = sanitizeForTTS(text);
  const wordCount = cleanText.split(/\s+/).length;
  const avgWordsPerMinute = 150; // Average TTS speed
  return Math.ceil((wordCount / avgWordsPerMinute) * 60);
};

/**
 * Pure text validation for TTS suitability
 */
export const validateTextForTTS = (text: string): { isValid: boolean; reason?: string } => {
  const cleanText = sanitizeForTTS(text);
  
  if (!cleanText || cleanText.length < 10) {
    return { isValid: false, reason: 'Text too short for TTS' };
  }
  
  if (cleanText.length > 4000) {
    return { isValid: false, reason: 'Text too long for TTS' };
  }
  
  return { isValid: true };
};

/**
 * Pure text preprocessing for optimal TTS
 */
export const preprocessTextForTTS = (text: string): string => {
  return sanitizeForTTS(text)
    .replace(/\b(Mr|Mrs|Dr|Prof)\./g, '$1') // Remove periods from titles
    .replace(/\bvs\./g, 'versus') // Expand abbreviations
    .replace(/\be\.g\./g, 'for example') // Expand e.g.
    .replace(/\bi\.e\./g, 'that is') // Expand i.e.
    .replace(/([.!?])\s*([A-Z])/g, '$1 $2'); // Ensure proper spacing
};

/**
 * Pure function to analyze language breakdown
 */
export const getLanguageBreakdown = (text: string) => {
  const cleanText = sanitizeForTTS(text);
  const lines = cleanText.split('\n').filter(Boolean);
  
  let englishLines = 0;
  let chineseLines = 0;
  let mixedLines = 0;
  
  lines.forEach(line => {
    const hasEnglish = /[a-zA-Z]/.test(line);
    const hasChinese = /[\u4e00-\u9fa5]/.test(line);
    
    if (hasEnglish && hasChinese) {
      mixedLines++;
    } else if (hasEnglish) {
      englishLines++;
    } else if (hasChinese) {
      chineseLines++;
    }
  });
  
  return {
    total: lines.length,
    english: englishLines,
    chinese: chineseLines,
    mixed: mixedLines,
    hasEnglish: englishLines > 0 || mixedLines > 0,
    hasChinese: chineseLines > 0 || mixedLines > 0
  };
};

// Add missing exports for AbielTextProcessor
export interface TextMetrics {
  wordCount: number;
  characterCount: number;
  estimatedDuration: number;
  readingTime: string;
}

export interface LanguageBreakdown {
  total: number;
  english: number;
  chinese: number;
  mixed: number;
  hasEnglish: boolean;
  hasChinese: boolean;
}

/**
 * Get comprehensive text metrics
 */
export const getTextMetrics = (text: string): TextMetrics => {
  const cleanText = sanitizeForTTS(text);
  const wordCount = cleanText.split(/\s+/).length;
  const characterCount = cleanText.length;
  const estimatedDuration = estimateDuration(text);
  
  const minutes = Math.floor(estimatedDuration / 60);
  const seconds = Math.floor(estimatedDuration % 60);
  const readingTime = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  
  return {
    wordCount,
    characterCount,
    estimatedDuration,
    readingTime
  };
};
